import {useEffect, useMemo, useState} from "react";
import {cn} from "@/ReactInit/Scripts/DashboardPage/utils/dashboardHelpers";

export interface DashboardInputProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  placeholder?: string;
  required?: boolean;
  validate?: (value: string) => string | null;
  prefix?: React.ReactNode;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  autoComplete?: string;
  forceTouched?: boolean;
  onTouchedChange?: (touched: boolean) => void;
}

const DashboardInput: React.FC<DashboardInputProps> = (
  {
    id,
    label,
    value,
    onChange,
    type = "text",
    placeholder = "",
    required = false,
    validate,
    prefix,
    inputMode,
    autoComplete,
    forceTouched,
    onTouchedChange,
  }) => {
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (forceTouched) setTouched(true);
  }, [forceTouched]);

  const error = useMemo(() => {
    if (!validate) return null;
    return validate(value);
  }, [validate, value]);

  const showError = touched && !!error;

  return (
    <div className="flex flex-col gap-2 w-full">
      <label htmlFor={id} className="text-xl font-semibold">
        {label}
        {required && <span className="text-red-secondary"> *</span>}
      </label>

      <div className="relative w-full">
        {prefix && (
          <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none">
            {prefix}
          </div>
        )}

        <input
          id={id}
          type={type}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          onBlur={() => {
            setTouched(true);
            onTouchedChange?.(true);
          }}
          required={required}
          aria-required={required}
          aria-invalid={showError ? true : undefined}
          aria-describedby={showError ? `${id}-error` : undefined}
          inputMode={inputMode}
          autoComplete={autoComplete}
          className={cn(
            "w-full py-3 px-6 text-lg bg-white border-2 border-brown-primary rounded-xl placeholder-black-muted placeholder:opacity-60 text-black-text truncate",
            prefix ? "pl-12" : "",
            showError ? "border-red-secondary" : ""
          )}
        />
      </div>

      {showError && (
        <p id={`${id}-error`} className="px-1 text-sm text-red-secondary">
          {error}
        </p>
      )}
    </div>
  );
};

export default DashboardInput;
